import { useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";

// Multi-tenant SaaS: Configurable namespace from environment variable
// Fallback to app.forcemx.io for multi-tenant compatibility
const NAMESPACE = import.meta.env.VITE_AUTH0_NAMESPACE || "https://app.forcemx.io";

export interface UserRoles {
  roles: string[];
  hasRole: (role: string) => boolean;
  isSales: boolean;
  isTeam: boolean;
  isAdmin: boolean;
}

export const useUserRoles = (): UserRoles => {
  const { user, isAuthenticated } = useAuth0();

  const userRoles = useMemo(() => {
    if (!isAuthenticated || !user) {
      return {
        roles: [],
        hasRole: () => false,
        isSales: false,
        isTeam: false,
        isAdmin: false,
      };
    }

    // Extract roles from custom claims using configurable namespace
    const roles = user[`${NAMESPACE}/roles`] || [];

    const hasRole = (role: string): boolean => {
      return roles.includes(role);
    };

    // Multi-tenant SaaS: Support both generic and legacy role names
    // Generic roles: "sales", "team", "admin"
    // Legacy roles: "E&H Immigration Sales", "E&H Immigration Team", "E&H Admin"
    return {
      roles,
      hasRole,
      isSales: hasRole("sales") || hasRole("Sales") || hasRole("E&H Immigration Sales"),
      isTeam: hasRole("team") || hasRole("Team") || hasRole("E&H Immigration Team"),
      isAdmin: hasRole("admin") || hasRole("Admin") || hasRole("E&H Admin"),
    };
  }, [user, isAuthenticated]);

  return userRoles;
};
